import { useEffect, useId, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";

import type { GallerySchoolList } from "@canvaschool/contracts";
import type { KeyboardEvent } from "react";

export type GallerySchool = GallerySchoolList["schools"][number];

const DEBOUNCE_MILLISECONDS = 300;
const MIN_QUERY_LENGTH = 2;
const MAX_QUERY_LENGTH = 40;

type SearchState = "idle" | "loading" | "done" | "error";

/**
 * Like SchoolPicker, but it lists only schools that have gallery posts and
 * shows how many each has. It does not hold the pick; the page does.
 */
export const GallerySchoolPicker = ({
  id,
  onChange,
}: {
  id: string;
  onChange: (school: GallerySchool) => void;
}) => {
  const { api } = useAuth();
  const listId = `${id}-list`;
  const statusId = useId();
  const [query, setQuery] = useState("");
  const [schools, setSchools] = useState<GallerySchool[]>([]);
  const [state, setState] = useState<SearchState>("idle");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const text = query.trim();
  const searchable = text.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!searchable) {
      setState("idle");
      setSchools([]);
      setActive(-1);
      return undefined;
    }
    setState("loading");
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      api
        .searchGallerySchools(
          text.slice(0, MAX_QUERY_LENGTH),
          controller.signal,
        )
        .then((found) => {
          if (controller.signal.aborted) {
            return;
          }
          setSchools(found);
          setActive(-1);
          setState("done");
          setOpen(true);
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setSchools([]);
            setState("error");
            setOpen(true);
          }
        });
    }, DEBOUNCE_MILLISECONDS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [api, text, searchable]);

  const choose = (school: GallerySchool) => {
    setOpen(false);
    onChange(school);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (schools.length === 0) {
        return;
      }
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) =>
        current === -1
          ? step === 1
            ? 0
            : schools.length - 1
          : (current + step + schools.length) % schools.length,
      );
    } else if (event.key === "Enter") {
      if (open && active >= 0 && schools[active]) {
        event.preventDefault();
        choose(schools[active]);
      } else if (open && state !== "idle") {
        event.preventDefault();
      }
    } else if (event.key === "Escape" && open) {
      event.stopPropagation();
      setOpen(false);
    }
  };

  const listVisible = open && state === "done" && schools.length > 0;
  const message =
    state === "loading"
      ? "찾는 중…"
      : state === "error"
      ? "학교를 찾지 못했습니다. 잠시 후 다시 입력해 주세요."
      : state === "done" && schools.length === 0
      ? "작품이 올라온 학교가 없습니다. 학교 이름 일부만 넣어 보세요."
      : !searchable && text.length > 0
      ? "두 글자 이상 입력해 주세요."
      : null;

  return (
    <div className="canvaschool-school-picker">
      <input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listVisible}
        aria-controls={listId}
        aria-activedescendant={
          listVisible && active >= 0 ? `${id}-opt-${active}` : undefined
        }
        aria-describedby={message ? statusId : undefined}
        autoComplete="off"
        maxLength={MAX_QUERY_LENGTH}
        placeholder="학교 이름 검색 (예: 성거초)"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      <ul
        id={listId}
        role="listbox"
        aria-label="작품이 있는 학교"
        className="canvaschool-school-list"
        hidden={!listVisible}
      >
        {listVisible &&
          schools.map((school, index) => (
            <li
              key={school.code}
              id={`${id}-opt-${index}`}
              role="option"
              aria-selected={index === active}
              className={
                index === active
                  ? "canvaschool-school-option canvaschool-school-option--active"
                  : "canvaschool-school-option"
              }
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(school)}
            >
              <strong>{school.name}</strong>
              <small>
                {[school.officeName, `작품 ${school.postCount}개`]
                  .filter((part) => part !== "")
                  .join(" · ")}
              </small>
            </li>
          ))}
      </ul>
      <p
        id={statusId}
        role="status"
        className={
          state === "error"
            ? "canvaschool-error"
            : "canvaschool-hint canvaschool-school-status"
        }
      >
        {message}
      </p>
    </div>
  );
};
